import { axios } from "../data/axios";
import { notify } from "./notify";

/**
 * Shared document download.
 *
 * Both document areas previously imported plain `axios` for this. That is the
 * same instance `data/axios` attaches the global response interceptor to, so
 * a stale or expired signed media URL answering 404 navigated the user off to
 * the NotFound page in the middle of a list. `skipGlobalErrorHandler` opts the
 * media request out and leaves the failure as a toast, which is what a failed
 * download should be.
 *
 * Nothing here logs the URL: these are signed links and the ISMS rules treat
 * them as credentials.
 */

/** Filename from a signed media URL, query string and encoding removed. */
export const fileNameFromUrl = (url?: string, fallback = "document") => {
  if (!url) return fallback;
  try {
    const clean = url.split("?")[0];
    return decodeURIComponent(clean.split("/").pop() || fallback);
  } catch {
    return url.split("?")[0].split("/").pop() || fallback;
  }
};

/**
 * Extension by MIME type, for the common cases verify-service serves back
 * (`document_capture_type` is Image/PDF). Callers pass a human-readable
 * label as `fileName` (e.g. "Tax assessment notice"), which has no
 * extension of its own — without this, the anchor's `download` attribute
 * has neither an extension nor a Blob MIME type to infer one from, and the
 * browser falls back to saving the file as `.txt` regardless of the actual
 * content.
 */
const EXTENSION_BY_MIME: Record<string, string> = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/tiff": "tiff",
  "image/bmp": "bmp",
};

const hasExtension = (name: string) => /\.[a-z0-9]{2,5}$/i.test(name);

const resolveFileName = (
  fileName: string,
  url: string,
  contentType?: string
) => {
  if (hasExtension(fileName)) return fileName;

  const mimeExt = contentType && EXTENSION_BY_MIME[contentType];
  if (mimeExt) return `${fileName}.${mimeExt}`;

  const urlExt = fileNameFromUrl(url, "").match(/\.[a-z0-9]{2,5}$/i);
  return urlExt ? `${fileName}${urlExt[0]}` : fileName;
};

export const downloadDocument = async (
  url: string,
  authHeader: string,
  fileName?: string
) => {
  const response = await axios.get(url, {
    headers: { Authorization: authHeader },
    responseType: "arraybuffer",
    skipGlobalErrorHandler: true,
  } as any);

  const contentType = (response.headers?.["content-type"] as string)
    ?.split(";")[0]
    ?.trim();

  const blob = new Blob(
    [response.data],
    contentType ? { type: contentType } : undefined
  );
  const objectUrl = window.URL.createObjectURL(blob);

  const link = window.document.createElement("a");
  link.href = objectUrl;
  link.download = resolveFileName(
    fileName || fileNameFromUrl(url),
    url,
    contentType
  );
  window.document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(objectUrl);
};

/**
 * Download several documents one after another.
 *
 * Sequential on purpose: browsers rate-limit and silently drop concurrent
 * programmatic downloads, so the previous `Promise.all` version could report
 * success having saved only some of the files. A single toast reports how many
 * actually landed.
 */
export const downloadDocuments = async (
  documents: { media?: string; fileName?: string }[],
  authHeader: string
) => {
  const downloadable = documents.filter((d) => d.media);

  if (!downloadable.length) {
    notify("There are no documents to download.", {
      type: "info",
      position: "bottom-left",
    });
    return;
  }

  notify(
    `Downloading ${downloadable.length} document${
      downloadable.length === 1 ? "" : "s"
    }, this may take a few seconds.`,
    { type: "info", position: "bottom-left" }
  );

  let failed = 0;

  for (const document of downloadable) {
    try {
      await downloadDocument(
        document.media as string,
        authHeader,
        document.fileName
      );
    } catch {
      failed += 1;
    }
  }

  if (failed) {
    notify(
      `${failed} of ${downloadable.length} documents could not be downloaded.`,
      { type: "error", position: "bottom-left" }
    );
    return;
  }

  notify("All documents downloaded.", {
    type: "success",
    position: "bottom-left",
  });
};
